import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { child, get, ref, set } from 'firebase/database';

import { ChatFireService } from '../gen2ai/chatfire.service';
import { Message, Conversation } from '../models/chat.model';
import { database } from '../gen2ai/fire-init';

export type CoachStatus = 'pending' | 'ready' | 'error' | 'skipped';

export type CoachSuggestion = {
  modelId: string;
  modelName?: string;
  reason: string;
  tradeoffs?: string;
  valueNote?: string;
  speedNote?: string;
  qualityNote?: string;
  relevanceNote?: string;
  estimatedTokens?: number | null;
};

export type CoachEntry = {
  id: string;
  userText: string;
  assistantText: string;
  assistantModelId?: string;
  assistantModelName?: string;
  tokensUsed?: number;
  createdAt: string;
  status: CoachStatus;
  summary?: string;
  suggestions?: CoachSuggestion[];
  errorMessage?: string;
  sessionId?: string;
  uid?: string;
};

type ModelCatalogEntry = {
  modelId: string;
  vendor:
    | 'OpenAI'
    | 'AWS'
    | 'Anthropic'
    | 'Meta'
    | 'Google'
    | 'Cohere'
    | 'Writer'
    | 'Mistral'
    | 'AI21'
    | 'DeepSeek'
    | 'xAI'
    | 'Z.ai';
  vendorName: string;
  officialName: string;
  shortLabel: string;
};

const MODEL_CATALOG: Record<string, ModelCatalogEntry> = {
  chatgpt52: {
    modelId: 'chatgpt52',
    vendor: 'OpenAI',
    vendorName: 'GPT-5.2',
    officialName: 'GPT-5.2',
    shortLabel: 'GPT-5.2'
  },
  palmx: {
    modelId: 'palmx',
    vendor: 'Writer',
    vendorName: 'Palmyra X5',
    officialName: 'Palmyra X5',
    shortLabel: 'Palmyra X5'
  },
  gemini3flash: {
    modelId: 'gemini3flash',
    vendor: 'Google',
    vendorName: 'Gemini Flash 3',
    officialName: 'Gemini 3 Flash',
    shortLabel: 'Gemini Flash 3'
  },
  deepseek: {
    modelId: 'deepseek',
    vendor: 'DeepSeek',
    vendorName: 'DeepSeek R1',
    officialName: 'DeepSeek-R1',
    shortLabel: 'DeepSeek R1'
  },
  deepv3: {
    modelId: 'deepv3',
    vendor: 'DeepSeek',
    vendorName: 'DeepSeek V3.2',
    officialName: 'DeepSeek-V3.2',
    shortLabel: 'DeepSeek V3.2'
  },
  o3: {
    modelId: 'o3',
    vendor: 'OpenAI',
    vendorName: 'OpenAI o3',
    officialName: 'OpenAI o3',
    shortLabel: 'o3'
  },
  grok41: {
    modelId: 'grok41',
    vendor: 'xAI',
    vendorName: 'Grok 4.1',
    officialName: 'Grok 4.1',
    shortLabel: 'Grok 4.1'
  },
  glm47: {
    modelId: 'glm47',
    vendor: 'Z.ai',
    vendorName: 'GLM-4.7',
    officialName: 'GLM-4.7',
    shortLabel: 'GLM-4.7'
  },
  novapro: {
    modelId: 'novapro',
    vendor: 'AWS',
    vendorName: 'Nova Pro',
    officialName: 'Amazon Nova Pro',
    shortLabel: 'Nova Pro'
  },
  maverick: {
    modelId: 'maverick',
    vendor: 'Meta',
    vendorName: 'Llama 4',
    officialName: 'Llama 4',
    shortLabel: 'Llama 4'
  },
  opus45: {
    modelId: 'opus45',
    vendor: 'Anthropic',
    vendorName: 'Claude Opus 4.5',
    officialName: 'Claude Opus 4.5',
    shortLabel: 'Opus 4.5'
  },
  haiku45: {
    modelId: 'haiku45',
    vendor: 'Anthropic',
    vendorName: 'Claude Haiku 4.5',
    officialName: 'Claude Haiku 4.5',
    shortLabel: 'Haiku 4.5'
  },
  sonnet45: {
    modelId: 'sonnet45',
    vendor: 'Anthropic',
    vendorName: 'Sonnet 4.5',
    officialName: 'Sonnet 4.5',
    shortLabel: 'Sonnet 4.5'
  },
  cohere: {
    modelId: 'cohere',
    vendor: 'Cohere',
    vendorName: 'Command R+',
    officialName: 'Command R+',
    shortLabel: 'Command R+'
  },
  gemini: {
    modelId: 'gemini',
    vendor: 'Google',
    vendorName: 'Gemini',
    officialName: 'Gemini (model family)',
    shortLabel: 'Gemini'
  },
  jamba: {
    modelId: 'jamba',
    vendor: 'AI21',
    vendorName: 'Jamba Large',
    officialName: 'Jamba Large',
    shortLabel: 'Jamba Large'
  },
  mistrallarge: {
    modelId: 'mistrallarge',
    vendor: 'Mistral',
    vendorName: 'Mistral Large',
    officialName: 'Mistral Large 2',
    shortLabel: 'Mistral Large'
  },
  pixtralimage: {
    modelId: 'pixtralimage',
    vendor: 'Mistral',
    vendorName: 'Pixtral',
    officialName: 'Pixtral Large',
    shortLabel: 'Pixtral'
  }
};

const modelLabelFor = (modelId: string): string =>
  MODEL_CATALOG[modelId]?.shortLabel || MODEL_CATALOG[modelId]?.vendorName || modelId;

type CoachLambdaResponse = {
  status?: CoachStatus;
  summary?: string;
  suggestions?: CoachSuggestion[];
  errorMessage?: string;
};

@Injectable({ providedIn: 'root' })
export class ClarityCoachService {
  /** Primary Clarity Coach endpoint. */
  private readonly coachEndpointDeepSeek =
    'https://5lsrnlddujh2uxtg6rgq47ok540gvrgo.lambda-url.us-east-1.on.aws/';
  /** Legacy Opus/DeepCoach – no longer used as primary. */
  private readonly coachEndpointDeepCoach =
    'https://5lsrnlddujh2uxtg6rgq47ok540gvrgo.lambda-url.us-east-1.on.aws/';
  private readonly coachEndpointHaiku =
    'https://3xan3djpur67gcozvnstscvcrq0jypfk.lambda-url.us-east-1.on.aws/';

  assistantSonnetCoachEndpoint: string = 'https://5lsrnlddujh2uxtg6rgq47ok540gvrgo.lambda-url.us-east-1.on.aws/';
  private readonly entriesSubject = new BehaviorSubject<CoachEntry[]>([]);
  readonly entries$ = this.entriesSubject.asObservable();

  private readonly historicalEntriesSubject = new BehaviorSubject<CoachEntry[]>([]);
  readonly historicalEntries$ = this.historicalEntriesSubject.asObservable();

  private readonly preferenceSubject = new BehaviorSubject<number>(40);
  readonly preference$ = this.preferenceSubject.asObservable();

  private readonly processedAssistantIds = new Set<string>();
  private conversationSub?: Subscription;
  private allConversationsSub?: Subscription;
  private uidSub?: Subscription;
  private sessionSub?: Subscription;
  private readonly fallbackDelayMs = 1500;
  private readonly rateLimitDelayMs = 2500;
  private readonly minCoachMessageLength = 200;
  private readonly coachRequestDelayMs = 900;
  private currentUid: string | null = null;
  private currentSessionId: string | null = null;

  constructor(
    private chatService: ChatFireService,
    private http: HttpClient
  ) {
    this.conversationSub = this.chatService.currentConversation$.subscribe((conv) => {
      this.ingestConversation(conv);
    });

    this.allConversationsSub = this.chatService.allConversationsByUid$.subscribe((conversations) => {
      conversations.forEach((conversation) => {
        this.ingestConversation(conversation);
      });
    });

    this.uidSub = this.chatService.uid$.subscribe(uid => {
      this.currentUid = uid;
    });

    this.sessionSub = this.chatService.sessionId$.subscribe(sessionId => {
      this.currentSessionId = sessionId;
    });
  }

  setPreference(value: number): void {
    const normalized = Math.max(0, Math.min(100, Math.round(value)));
    this.preferenceSubject.next(normalized);
  }

  getPreference(): number {
    return this.preferenceSubject.value;
  }

  getRecentCoachEntries(limit = 10): CoachEntry[] {
    return [...this.entriesSubject.value]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit);
  }

  private ingestConversation(conv: Conversation | null): void {
    if (!conv?.messages?.length) return;

    const messages = conv.messages;
    const latestAssistantIndex = this.findLatestAssistantIndex(messages);

    messages.forEach((msg, index) => {
      if (index !== latestAssistantIndex) return;
      if (msg.role !== 'assistant') return;
      if (!msg.id || this.processedAssistantIds.has(msg.id)) return;

      const userMsg = this.findPriorUser(messages, index);
      if (!userMsg) {
        this.processedAssistantIds.add(msg.id);
        return;
      }

      const userText = this.normalizeMessageText(userMsg.content);
      const assistantText = this.normalizeMessageText(msg.content);
      const eligibleForCoachEndpoint = this.canCallCoachEndpoint(userText, assistantText, msg);
      const entry: CoachEntry = {
        id: msg.id,
        userText,
        assistantText,
        assistantModelId: msg.modelId,
        assistantModelName: msg.modelDisplayName || undefined,
        tokensUsed: typeof msg.tokens === 'number' ? msg.tokens : undefined,
        createdAt: new Date(msg.timestamp || new Date()).toISOString(),
        status: 'pending',
        sessionId: msg.sessionId,
        uid: msg.uid
      };

      const shouldDisplay = this.shouldDisplayEntry(entry);

      if (!userText || !assistantText) {
        entry.status = 'skipped';
        entry.errorMessage = 'No content to analyze.';
        if (shouldDisplay) {
          this.pushEntry(entry);
        }
        this.processedAssistantIds.add(msg.id);
        return;
      }

      if (this.isErrorResponse(assistantText)) {
        entry.status = 'skipped';
        entry.errorMessage = 'Assistant response looked like an error.';
        if (shouldDisplay) {
          this.pushEntry(entry);
        }
        this.processedAssistantIds.add(msg.id);
        return;
      }

      if (!eligibleForCoachEndpoint) {
        entry.status = 'skipped';
        entry.errorMessage = `Requires upstream 200 OK plus user and assistant text each >= ${this.minCoachMessageLength} characters.`;
        if (shouldDisplay) {
          this.pushEntry(entry);
        }
        this.processedAssistantIds.add(msg.id);
        return;
      }

      if (shouldDisplay) {
        this.pushEntry(entry);
        setTimeout(() => void this.requestCoach(entry), this.coachRequestDelayMs);
      }
      this.processedAssistantIds.add(msg.id);
    });
  }
  private canCallCoachEndpoint(userText: string, assistantText: string, assistantMsg: Message): boolean {
    if (userText.trim().length < this.minCoachMessageLength) return false;
    if (assistantText.trim().length < this.minCoachMessageLength) return false;

    const candidateStatus =
      (assistantMsg as any).statusCode
      ?? (assistantMsg as any).httpStatus
      ?? (assistantMsg as any).responseStatus
      ?? (assistantMsg as any).status;

    const hasNumericStatus = Number.isFinite(Number(candidateStatus));
    if (hasNumericStatus) {
      return Number(candidateStatus) === 200;
    }

    const okFlag = (assistantMsg as any).ok;
    if (typeof okFlag === 'boolean') return okFlag;

    // Strict mode: do not call endpoint when upstream success metadata is absent.
    return false;
  }

  private findLatestAssistantIndex(messages: Message[]): number {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return i;
    }
    return -1;
  }

  private normalizeMessageText(content: unknown): string {
    if (typeof content === 'string') return content.trim();
    if (content == null) return '';
    try {
      return JSON.stringify(content).trim();
    } catch {
      return String(content).trim();
    }
  }

  private shouldDisplayEntry(entry: CoachEntry): boolean {
    if (!this.currentSessionId) return true;
    if (!entry.sessionId) return true;
    return entry.sessionId === this.currentSessionId;
  }

  private findPriorUser(messages: Message[], index: number): Message | null {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'user') {
        return messages[i] as Message;
      }
    }
    return null;
  }

  private isErrorResponse(text: string): boolean {
    const lowered = text.toLowerCase();
    if (lowered.includes('sorry, i encountered an error')) return true;
    if (/(\b4\d\d\b|\b5\d\d\b)/.test(lowered)) return true;
    if (lowered.includes('forbidden') || lowered.includes('unauthorized')) return true;
    return false;
  }

  private pushEntry(entry: CoachEntry): void {
    const next = [entry, ...this.entriesSubject.value];
    this.entriesSubject.next(next.slice(0, 10));
  }

  private updateEntry(entryId: string, patch: Partial<CoachEntry>): void {
    const next = this.entriesSubject.value.map((entry) =>
      entry.id === entryId ? { ...entry, ...patch } : entry
    );
    this.entriesSubject.next(next);
  }

  async loadHistoricalEntries(limit = 10): Promise<boolean> {
    const uid = this.currentUid;
    if (!uid) {
      this.historicalEntriesSubject.next([]);
      return false;
    }

    try {
      const snapshot = await get(child(ref(database), `coachEntries/${uid}`));
      if (!snapshot.exists()) {
        this.historicalEntriesSubject.next([]);
        return true;
      }

      const data = snapshot.val() as Record<string, CoachEntry>;
      const entries = Object.values(data)
        .filter(entry => !!entry?.id)
        .map(entry => ({
          ...entry,
          createdAt: entry.createdAt || new Date().toISOString()
        }))
        .filter(entry => entry.sessionId !== this.currentSessionId)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, limit);

      this.historicalEntriesSubject.next(entries);
      return true;
    } catch (err) {
      console.error('[ClarityCoachService] loadHistoricalEntries error', err);
      this.historicalEntriesSubject.next([]);
      return false;
    }
  }

  private async persistCoachEntry(entry: CoachEntry): Promise<void> {
    if (!entry.id) return;
    const uid = entry.uid ?? this.currentUid;
    if (!uid) return;

    try {
      const payload = this.sanitizeFirebasePayload({
        ...entry,
        uid,
        sessionId: entry.sessionId ?? this.currentSessionId ?? undefined
      });
      await set(ref(database, `coachEntries/${uid}/${entry.id}`), payload);
    } catch (err) {
      console.error('[ClarityCoachService] persistCoachEntry error', err);
    }
  }

  private async requestCoach(entry: CoachEntry): Promise<void> {
    const preference = this.preferenceSubject.value;
    const payload = {
      userText: entry.userText,
      assistantText: entry.assistantText,
      preference,
      inferenceConfig: {
        maxTokens: 1500,
        temperature: 0.3,
        stopSequences: []
      }
    };

    try {
      const response = await this.requestCoachWithFallback(payload);
      await this.handleCoachResponse(entry, response);
    } catch (err: any) {
      this.updateEntry(entry.id, {
        status: 'error',
        errorMessage: err?.message ?? 'Coach request failed.'
      });
    }
  }

  private async requestCoachWithFallback(
    payload: Record<string, unknown>
  ): Promise<CoachLambdaResponse> {
    return firstValueFrom(
      this.http.post<CoachLambdaResponse>(this.coachEndpointDeepSeek, payload)
    );
  }

  /**
   * Stub for double-coach menu: calls the same DeepSeek-based coach endpoint.
   * Replace or extend this when you add dedicated double-coach logic.
   */
  async requestDoubleCoachDeepSeek(payload: Record<string, unknown>): Promise<CoachLambdaResponse> {
    const userText = String(payload?.['userText'] ?? '').trim();
    const assistantText = String(payload?.['assistantText'] ?? '').trim();
    const statusCode = Number(payload?.['statusCode']);
    const hasStatusCode = Number.isFinite(statusCode);
    const okFlag = payload?.['ok'];

    const hasValidAssistantText = assistantText.length > this.minCoachMessageLength;
    const isSuccessfulCall = hasStatusCode ? statusCode === 200 : (typeof okFlag === 'boolean' ? okFlag : true);

    if (!userText || !hasValidAssistantText || !isSuccessfulCall) {
      throw new Error(
        `Double-coach request blocked: requires upstream 200 OK and assistant text longer than ${this.minCoachMessageLength} characters.`
      );
    }

    return firstValueFrom(
      this.http.post<CoachLambdaResponse>(this.coachEndpointDeepSeek, payload)
    );
  }

  private async handleCoachResponse(
    entry: CoachEntry,
    response: CoachLambdaResponse
  ): Promise<void> {
    if (response?.status === 'skipped') {
      const updated: CoachEntry = {
        ...entry,
        status: 'skipped',
        errorMessage: response.errorMessage ?? 'Assistant response looked like an error.',
        sessionId: entry.sessionId ?? this.currentSessionId ?? undefined,
        uid: entry.uid ?? this.currentUid ?? undefined
      };
      this.updateEntry(entry.id, updated);
      await this.persistCoachEntry(updated);
      return;
    }

    if (!response || response.status !== 'ready') {
      throw new Error('Coach response was not ready.');
    }

    const suggestions = Array.isArray(response.suggestions)
      ? response.suggestions
          .map((item) => this.normalizeSuggestion(item))
          .filter((item): item is CoachSuggestion => !!item)
      : [];
    const updated: CoachEntry = {
      ...entry,
      status: 'ready',
      summary: response.summary ?? '',
      suggestions,
      sessionId: entry.sessionId ?? this.currentSessionId ?? undefined,
      uid: entry.uid ?? this.currentUid ?? undefined
    };
    this.updateEntry(entry.id, updated);
    await this.persistCoachEntry(updated);
  }

  private normalizeSuggestion(item: any): CoachSuggestion | null {
    if (!item || typeof item !== 'object') return null;
    const modelId = String(item.modelId || '').trim();
    if (!modelId) return null;

    return {
      modelId,
      modelName: modelLabelFor(modelId) ?? item.modelName,
      reason: String(item.reason || '').trim(),
      tradeoffs: String(item.tradeoffs || '').trim() || undefined,
      valueNote: String(item.valueNote || '').trim() || undefined,
      speedNote: String(item.speedNote || '').trim() || undefined,
      qualityNote: String(item.qualityNote || '').trim() || undefined,
      relevanceNote: String(item.relevanceNote || '').trim() || undefined
    };
  }

  private sanitizeFirebasePayload<T extends Record<string, unknown>>(payload: T): T {
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined)
    ) as T;
  }

  private async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  private getRetryDelay(err: any): number {
    const status = err?.status ?? err?.error?.status;
    if (status === 429) {
      return this.rateLimitDelayMs + Math.floor(Math.random() * 500);
    }
    if (status >= 500) {
      return this.fallbackDelayMs + Math.floor(Math.random() * 300);
    }
    return 750;
  }
}
